// Header, faithful port of src/app/venue/layout.tsx's header block in
// the Next.js app. Logo (image or initial fallback) on the left,
// circular profile-icon link on the right. Signed-out users see a
// "Sign in" pill instead.
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { initialFor, useVenue } from "../lib/venue";
import { useAuth } from "../lib/auth";
import { getMyNotifications } from "../lib/api";

export function VenueHeader() {
  const { data: venueData } = useVenue();
  const { user } = useAuth();
  const venue = venueData?.venue;

  // Unread notifications → badge on the bell. Shares the ["notifications"]
  // cache with the inbox page, so opening the page clears the badge.
  const notifQuery = useQuery({
    queryKey: ["notifications"],
    queryFn: getMyNotifications,
    enabled: !!user,
    retry: false,
  });
  const unreadCount = (notifQuery.data ?? []).filter((n) => !n.read).length;

  const venueName = venue?.appName ?? venue?.name ?? "";
  const logoSrc = venue?.appIconUrl || venue?.imageUrl || null;

  return (
    <header className="venue-header" aria-label="Top">
      <Link to="/" aria-label={venueName} className="venue-header-logo">
        {logoSrc ? (
          <img src={logoSrc} alt={venueName} />
        ) : (
          <span className="venue-header-initial">{initialFor(venueName)}</span>
        )}
      </Link>

      {user ? (
        <div className="venue-header-right">
          <Link
            to="/notifications"
            className="venue-header-bell"
            aria-label={
              unreadCount > 0
                ? `Notifications, ${unreadCount} unread`
                : "Notifications"
            }
          >
            <BellIcon />
            {unreadCount > 0 ? (
              <span className="venue-header-badge" aria-hidden>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            ) : null}
          </Link>
          <Link
            to="/account"
            className="venue-header-profile"
            aria-label="Profile"
            title={user.email}
          >
            <ProfileIcon />
          </Link>
        </div>
      ) : (
        <Link to="/sign-in" className="venue-header-signin">
          Sign in
        </Link>
      )}
    </header>
  );
}

function BellIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="20"
      height="20"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
      />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="20"
      height="20"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
      />
    </svg>
  );
}
